import { useEffect, useRef, useState } from 'react';
import { useFormik, FormikProps } from 'formik';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { useGenericMutation } from '../../services/react-query/generic.mutation';
import { createUserSchema } from '../../schemas/create-user.schema';
import { CustomerTypes, UserTypes } from '../../types';
import { Team } from '../../types/teams.types';
import { Tag } from '../../types/tags.types';
import { RootState } from '../../store';

interface Args {
    selectedUser?: UserTypes.Model;
    allTeams: Team[];
    customerSettings: CustomerTypes.OnboardingForm;
    onSuccess(): void;
}

export interface UseUserFormikResult {
    formik: FormikProps<UserTypes.NewUserParams>;
    selectedTeam: Team | null;
    setSelectedTeam(t: Team | null): void;
    selectedTags: Tag[];
    setSelectedTags(t: Tag[]): void;
    availableTags: Tag[];
    isLoading: boolean;
    handlePhoneChange(value: string): void;
    isPhoneRequired: boolean;
    isEmployeeIdRequired: boolean;
    isEmailRequired: boolean;
}

export const useUserFormik = ({
    selectedUser,
    allTeams,
    customerSettings,
    onSuccess,
}: Args): UseUserFormikResult => {
    const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
    const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

    const { mutateAsync: addNewUser, isLoading } = useGenericMutation<
        UserTypes.CreateUserParams,
        UserTypes.Model
    >('addUser', 'users/addUser', 'post');

    const formik = useFormik<UserTypes.NewUserParams>({
        enableReinitialize: true,
        validationSchema: createUserSchema(customerSettings?.userUniqueIdentifier || ''),
        initialValues: selectedUser
            ? ({
                  firstName: selectedUser.firstName || '',
                  lastName: selectedUser.lastName || '',
                  phoneNumber: selectedUser.phoneNumber || '',
                  employeeId: selectedUser.employeeId || '',
                  backupEmail: selectedUser.backupEmail || '',
                  teamId: selectedUser.teamId || '',
                  tags: selectedUser.tags || [''],
              } as UserTypes.NewUserParams)
            : ({
                  firstName: '',
                  lastName: '',
                  phoneNumber: '',
                  employeeId: '',
                  backupEmail: '',
                  teamId: '',
                  tags: [''],
              } as UserTypes.NewUserParams),
        onSubmit: async (values) => {
            const teamId = selectedTeam ? (selectedTeam as any).id : null;
            const tags = selectedTags ? selectedTags.map((tag) => tag._id) : null;
            const { firstName, lastName, phoneNumber, employeeId, backupEmail } = values;
            const params: any = { firstName, lastName, employeeId, backupEmail };

            if (phoneNumber?.trim()?.length) params.phoneNumber = phoneNumber.trim();
            else params.phoneNumber = null;

            params.teamId = teamId || '';
            params.tags = (tags as string[]) || [''];

            const submitParams = {
                payload: params,
                uniqueId: customerSettings?.userUniqueIdentifier,
                userId: selectedUserId,
            } as UserTypes.CreateUserParams;

            try {
                await addNewUser(submitParams, {
                    onSuccess: (result) => {
                        toast(result.message, { autoClose: 1500, type: 'success' });
                        onSuccess();
                    },
                });
            } catch (err) {
                const errorMessage = (err as any)?.response?.data?.message;
                toast(
                    errorMessage ?? `Error ${selectedUserId ? 'updating' : 'adding new'} user`,
                    { autoClose: 7500, type: 'error' },
                );
            }
        },
    });

    const { items: availableTags } = useSelector((state: RootState) => state.tags);

    // Track which user we've already hydrated tags for. Without this, the
    // effect re-runs whenever availableTags updates (e.g. after creating a
    // new tag) and clobbers any locally-added selections with the user's
    // server-side tag list.
    const hydratedTagsForUserRef = useRef<string | null>(null);

    useEffect(() => {
        let userId = null;
        let teamId = '';
        const userObj = selectedUser as any;
        if (userObj && (userObj.id || userObj._id)) {
            // _id may arrive as either an object ({$oid: '...'}) or a raw
            // string depending on the call site, so fall back to the raw
            // string form before giving up.
            userId = userObj.id || userObj._id?.$oid || userObj._id;
            setSelectedUserId(userId);
            if (userObj.teamId) teamId = userObj.teamId?.$oid || userObj.teamId;
        }
        // Always reset selectedTeam when the user lookup doesn't resolve to
        // a team — otherwise switching from a user with a team to a user
        // without one would silently submit the previous user's team.
        if (teamId && teamId.length && allTeams?.length > 0) {
            setSelectedTeam(allTeams.find((team) => (team as any).id === teamId) || null);
        } else {
            setSelectedTeam(null);
        }
        if (
            selectedUser &&
            availableTags?.length > 0 &&
            hydratedTagsForUserRef.current !== userId
        ) {
            setSelectedTags(
                (availableTags.filter((tag) =>
                    selectedUser.tags?.includes(tag?._id!),
                ) as Tag[]) || [],
            );
            hydratedTagsForUserRef.current = userId;
        }
        if (!selectedUser) {
            setSelectedTags([]);
            setSelectedUserId(null);
            hydratedTagsForUserRef.current = null;
        }
    }, [selectedUser, allTeams, availableTags]);

    const handlePhoneChange = (value: string) => {
        let digitsOnly = value.replace(/\D/g, '');
        if (digitsOnly && !digitsOnly.startsWith('1')) digitsOnly = '1' + digitsOnly;
        if (digitsOnly.length > 11) digitsOnly = digitsOnly.slice(0, 11);
        let formattedValue = digitsOnly;
        if (formattedValue && !formattedValue.startsWith('+')) {
            formattedValue = '+' + formattedValue;
        }
        formik.setFieldValue('phoneNumber', formattedValue);
    };

    const isPhoneRequired =
        !customerSettings?.userUniqueIdentifier ||
        customerSettings?.userUniqueIdentifier === 'INVITECODE' ||
        customerSettings?.userUniqueIdentifier === 'PHONENUMBER';
    const isEmployeeIdRequired = customerSettings?.userUniqueIdentifier === 'EMPLOYEEID';
    const isEmailRequired = customerSettings?.userUniqueIdentifier === 'EMAILADDRESS';

    return {
        formik,
        selectedTeam,
        setSelectedTeam,
        selectedTags,
        setSelectedTags,
        availableTags: availableTags as Tag[],
        isLoading,
        handlePhoneChange,
        isPhoneRequired,
        isEmployeeIdRequired,
        isEmailRequired,
    };
};
